import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SourcingProjectRequestItemField } from '~/common/types/cubes/SourcingProjectRequestProposalItem';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  joinFormatterFactory,
  LinkCell,
  numberFormatterFactory,
  StatusChipCell,
  UseDataGridConfig,
  useDataGridMasterDetailConfig,
} from '~/components/DataGridCustom';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { supplierValueGetter } from '~/components/DataGridWidget/DataGridWidget.util';
import { IconId } from '~/components/Icon';

import {
  SourcingProjectItemStatus,
  SourcingProjectRequestDetailsDataGridRow,
  SourcingProjectRequestItemGridColumnGroup,
} from './SourcingProjectRequestListWidget.types';

export const useSourcingProjectRequestGridConfig: UseDataGridConfig<any> = () => {
  const { t } = useTranslation(undefined, { keyPrefix: `SourcingProjectRequestItemGrid` });
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'SourcingProjectRequestItemGrid.column' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'SourcingProjectRequestItemGrid.group' });

  const { defaultToggleColumnDefinition, ...masterDetailProps } =
    useDataGridMasterDetailConfig<SourcingProjectRequestDetailsDataGridRow>();

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      columnVisibilityModel: {},
      columns: [
        defaultToggleColumnDefinition,
        createColumn({ field: SourcingProjectRequestItemField.POSITION, width: 80 }),
        createColumn({ field: SourcingProjectRequestItemField.LOT_ITEM_TITLE, width: 300 }),
        createColumn({ field: SourcingProjectRequestItemField.LOT_TITLE, width: 300 }),
        createColumn({
          field: SourcingProjectRequestItemField.QUANTITY,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.DECIMAL }),
        }),
        createColumn({
          field: SourcingProjectRequestItemField.LOT_ITEM_UNIT,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: SourcingProjectRequestItemField.PRICE_PER_UNIT,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            value: row[SourcingProjectRequestItemField.PRICE_PER_UNIT],
            currency: row[SourcingProjectRequestItemField.CURRENCY],
          })),
        }),
        createColumn({
          field: SourcingProjectRequestItemField.STATUS,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(StatusChipCell, ({ row }) => {
            const status = row[SourcingProjectRequestItemField.STATUS];
            const isAwarded = status === SourcingProjectItemStatus.AWARDED;
            return {
              value: isAwarded ? t(`itemStatus.${status}`) : t(`itemStatus.fallback`),
              color: isAwarded ? ('success' as const) : ('default' as const),
            };
          }),
        }),
        createColumn({
          field: SourcingProjectRequestItemField.SUPPLIERS,
          renderCell: cellFactory(LinkCell, ({ row }) => ({
            value: supplierValueGetter(row.suppliers?.split(',')),
            needs: { permission: Permission.BaseSupplierList },
          })),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 300,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: SourcingProjectRequestItemGridColumnGroup.ITEM,
          renderHeaderGroup: headerGroupWithIcon(IconId.sourcingProjectItem),
          children: [
            //
            { field: SourcingProjectRequestItemField.POSITION },
            { field: SourcingProjectRequestItemField.LOT_ITEM_TITLE },
            { field: SourcingProjectRequestItemField.LOT_TITLE },
            { field: SourcingProjectRequestItemField.LOT_ITEM_UNIT },
            { field: SourcingProjectRequestItemField.QUANTITY },
          ],
        }),
        createGroup({
          groupId: SourcingProjectRequestItemGridColumnGroup.AWARDING,
          renderHeaderGroup: headerGroupWithIcon(IconId.sourcingProjectAwarding),
          children: [
            //
            { field: SourcingProjectRequestItemField.PRICE_PER_UNIT },
            { field: SourcingProjectRequestItemField.STATUS },
            { field: SourcingProjectRequestItemField.SUPPLIERS },
          ],
        }),
      ],
      ...masterDetailProps,
    } as DataGridConfig<any>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultToggleColumnDefinition, t, tColumn]);
};
